import React, { useCallback, useEffect, useState, useMemo } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import Button from '@app/react-common/components/button/Button';
import { Tab, Tabs } from '@app/react-common/components/tabs/Tabs';
import { useAppContext } from '@app/react-common/AppContext';
import useExtensionGroups from '@app/react-common/hooks/useExtensionGroups';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import {
  ExtensionsGroupType,
  ExtensionsGroupTypeToDisplayName,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { AbTest } from '../../models/AbTest';
import { AbTestVariant } from '../../models/AbTestVariant';
import { TrafficAllocationVariant } from '@app/react-common/components/traffic-allocation/TrafficAllocationShared';
import { AbTestWidgetType, AbTestTypeCategory } from '../../models/AbTestCreationType';
import { ExtensionsGroupsAllocationSection } from './components/ExtensionsGroupsAllocationSection';
import { TokensForShopifySection } from './components/TokensForShopifySection';
import { noneExtension } from '../list-ab-tests/AbTestCard/utils';
import { AbTestFormData, getInitialFormData } from './shared/abTestModalUtils';
import { AbTestModalHeader } from './shared/AbTestModalHeader';
import { AbTestModalDetailsTab } from './shared/AbTestModalDetailsTab';
import { AbTestModalRulesTab } from './shared/AbTestModalRulesTab';
import { AbTestModalTrafficAllocationTab } from './shared/AbTestModalTrafficAllocationTab';
import { useAbTestModalCommon } from './shared/useAbTestModalCommon';
import { RuleGroup } from '@app/react-common/components/rule-engine-v2/models/RuleGroup';
import { mapCheckoutSegmentRuleGroupsToRuleEngineState } from '@app/react-common/components/rule-engine-v2/utils/ruleEngineMapper';
import useExtensions from '@app/react-common/hooks/useExtensions';
import { IExtensionGroup } from '@app/pages/checkout-app/checkout-layout/main/components/extension-groups/models/extension-group.models';

interface AbTestModalExtensionGroupsProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  saveTest?: (formData: AbTest) => Promise<number | undefined>;
  existingAbTest?: AbTest;
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  onTestTypeChange?: (testType: AbTestTypeCategory) => void;
}

const TAB_ORDER = ['Details', 'Traffic allocation', 'Rules', 'Token for Shopify'];

const PRE_SELECTED_GROUP_TRAFFIC_PERCENTAGE = 50;

function resolveGroupType(group: IExtensionGroup): ExtensionsGroupType {
  return group.type ?? ExtensionsGroupType.EXTENSIONS;
}

// one test runs in one shopify block — the preselected pair must share a group type
function findFirstTwoGroupIdsSharingAGroupType(extensionGroups: IExtensionGroup[] | undefined): number[] {
  if (!extensionGroups?.length) {
    return [];
  }
  const sharedGroupType = resolveGroupType(extensionGroups[0]);
  return extensionGroups
    .filter((group) => resolveGroupType(group) === sharedGroupType)
    .slice(0, 2)
    .map((group) => group.id);
}

export const AbTestModalExtensionGroups: React.FC<AbTestModalExtensionGroupsProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  saveTest,
  existingAbTest,
  mode,
  onTestTypeChange,
}) => {
  const { shop$, showToast } = useAppContext();
  const [activeTab, setActiveTab] = useState('Details');
  const [shop, setShop] = useState<Shop>(null);
  const [trafficAllocationValid, setTrafficAllocationValid] = useState(false);
  const [rulesValid, setRulesValid] = useState(mode === 'create' ? false : true);
  const [statisticalSignificanceError, setStatisticalSignificanceError] = useState<string | undefined>(undefined);
  const [fullFormValid, setFullFormValid] = useState(false);
  const [showInvalidStep, setShowInvalidStep] = useState(0);
  const [formData, setFormData] = useState<AbTestFormData>(getInitialFormData(existingAbTest));
  const [testCreated, setTestCreated] = useState(mode === 'edit' || mode === 'view');

  // Reset validation state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowInvalidStep(0);
    }
  }, [isOpen]);
  const [savedAbTestId, setSavedAbTestId] = useState<number | null>(existingAbTest?.id || null);
  const { extensionGroups } = useExtensionGroups();
  const { extensions } = useExtensions();

  // Custom post-save handler: navigate to Token tab after creation for extension groups
  const handleAfterSave = useCallback(
    (testId: number | undefined) => {
      if ((mode === 'create' || mode === 'duplicate') && activeTab !== 'Token for Shopify') {
        setActiveTab('Token for Shopify');
      } else {
        onSaveSuccess();
      }
    },
    [mode, activeTab, setActiveTab, onSaveSuccess],
  );

  const {
    getNextTab,
    getPreviousTab,
    handleInputChange,
    setFormDataValue,
    handleSave,
    handleTabChange,
    handleDetailsValidation,
    handleRulesValidation,
    filteredRulesBasedOnTestTypeCatagory,
    checkoutApi,
  } = useAbTestModalCommon({
    mode,
    formData,
    setFormData,
    rulesValid,
    setRulesValid,
    setStatisticalSignificanceError,
    statisticalSignificanceError,
    widgetType: AbTestWidgetType.EXTENSION_GROUPS,
    shop,
    existingAbTest,
    saveTest,
    onSaveSuccess,
    onClose,
    activeTab,
    setActiveTab,
    TAB_ORDER,
    showToast,
    onAfterSave: handleAfterSave,
    setTestCreated,
    setSavedAbTestId,
  });

  // Track selected extension group IDs for dependency tracking
  const selectedExtensionGroupIds = useMemo(() => {
    const selected = formData?.abTestVariants?.filter((v) => v.isSelected && v.widgetExternalId !== null) || [];
    return selected
      .map((v) => v.widgetExternalId)
      .sort((a, b) => (a as number) - (b as number))
      .join(',');
  }, [formData?.abTestVariants]);

  // Clear abTestRules when extension groups change (for new AB tests only)
  useEffect(() => {
    if (!existingAbTest && formData?.abTestRules) {
      setFormData((prevData) => ({
        ...prevData,
        abTestRules: undefined,
      }));
    }
  }, [selectedExtensionGroupIds, existingAbTest]);

  // Extract and merge rule groups from selected extension groups' segmentRules (only for new AB tests)
  const initialRulesFromExtensionGroupsForNewABTest = useMemo(() => {
    if (existingAbTest || !extensionGroups || mode !== 'create') {
      return null;
    }

    const selectedVariants = formData?.abTestVariants?.filter((v) => v.isSelected && v.widgetExternalId !== null) || [];
    if (selectedVariants.length === 0) {
      return null;
    }

    const allRuleGroups: RuleGroup[] = [];
    selectedVariants.forEach((variant) => {
      const extensionGroup = extensionGroups.find((g) => g.id === variant.widgetExternalId);
      if (extensionGroup?.segmentRules) {
        extensionGroup.segmentRules.forEach((segmentRule) => {
          if (segmentRule.rules?.ruleGroups) {
            allRuleGroups.push(...segmentRule.rules.ruleGroups);
          }
        });
      }
    });

    if (allRuleGroups.length === 0) {
      return null;
    }

    return mapCheckoutSegmentRuleGroupsToRuleEngineState(allRuleGroups);
  }, [existingAbTest, extensionGroups, selectedExtensionGroupIds, mode, formData?.abTestVariants]);

  const preSelectedGroupIds = useMemo(
    () => (mode === 'create' ? findFirstTwoGroupIdsSharingAGroupType(extensionGroups) : []),
    [extensionGroups, mode],
  );

  // Memoize extension group variants mapping to avoid unnecessary recalculations
  const extensionGroupVariants = useMemo(() => {
    if (!extensionGroups) return [];
    return extensionGroups.map((group) => {
      const existingAbTestVariant: AbTestVariant = existingAbTest?.abTestVariants?.find(
        (variant) =>
          variant.abTestWidgets?.find((widget) => widget.type === AbTestWidgetType.EXTENSION_GROUPS)?.externalId ===
          group.id,
      );
      const isPreSelectedOnCreate = mode === 'create' && preSelectedGroupIds.includes(group.id);

      const trafficAllocationVariant: TrafficAllocationVariant = {
        apiId: null,
        widgetExternalId: group.id,
        name: group.name || `Extension Group ${group.id}`,
        description: group.description || '',
        groupTypeLabel: ExtensionsGroupTypeToDisplayName[resolveGroupType(group)],
        percentageTraffic: isPreSelectedOnCreate
          ? PRE_SELECTED_GROUP_TRAFFIC_PERCENTAGE
          : existingAbTestVariant?.percentageTraffic || 0,
        isSelected: mode === 'create' ? isPreSelectedOnCreate : Boolean(existingAbTestVariant),
        widgetApiId: undefined,
      };
      if (existingAbTestVariant) {
        trafficAllocationVariant.isSelected = true;
        trafficAllocationVariant.percentageTraffic = existingAbTestVariant.percentageTraffic;
        trafficAllocationVariant.apiId = existingAbTestVariant.id;
        const widget = existingAbTestVariant.abTestWidgets?.find((x) => x.type === AbTestWidgetType.EXTENSION_GROUPS);
        trafficAllocationVariant.widgetApiId = widget?.id;
        // Map variant name to slotIndex: 'A' = 0, 'B' = 1, 'C' = 2, etc.
        if (existingAbTestVariant.name && existingAbTestVariant.name.length === 1) {
          const charCode = existingAbTestVariant.name.charCodeAt(0);
          const aCharCode = 'A'.charCodeAt(0);
          if (charCode >= aCharCode && charCode <= 'Z'.charCodeAt(0)) {
            trafficAllocationVariant.slotIndex = charCode - aCharCode;
          }
        } else {
          trafficAllocationVariant.slotIndex = 0;
        }
      }

      return trafficAllocationVariant;
    });
  }, [extensionGroups, existingAbTest, mode, preSelectedGroupIds]);

  // Memoize NONE variant
  const noneVariant = useMemo(() => {
    const existingNoneVariant: AbTestVariant = existingAbTest?.abTestVariants?.find(
      (variant) =>
        variant.abTestWidgets?.find((widget) => widget.type === AbTestWidgetType.EXTENSION_GROUPS)?.externalId === null,
    );

    const variant: TrafficAllocationVariant = {
      ...noneExtension,
    };

    if (existingNoneVariant) {
      variant.isSelected = true;
      variant.percentageTraffic = existingNoneVariant.percentageTraffic;
      variant.apiId = existingNoneVariant.id;
      const widget = existingNoneVariant.abTestWidgets?.find((x) => x.type == AbTestWidgetType.EXTENSION_GROUPS);
      variant.widgetApiId = widget?.id;
      // Map variant name to slotIndex: 'A' = 0, 'B' = 1, 'C' = 2, etc.
      if (existingNoneVariant.name && existingNoneVariant.name.length === 1) {
        const charCode = existingNoneVariant.name.charCodeAt(0);
        const aCharCode = 'A'.charCodeAt(0);
        if (charCode >= aCharCode && charCode <= 'Z'.charCodeAt(0)) {
          variant.slotIndex = charCode - aCharCode;
        }
      } else {
        variant.slotIndex = 0;
      }
    }

    return variant;
  }, [existingAbTest]);

  // Populate form data from extension groups
  useEffect(() => {
    if (extensionGroupVariants.length > 0 || noneVariant) {
      setFormData((prevData) => ({
        ...prevData,
        abTestVariants: [...extensionGroupVariants, noneVariant],
      }));
    }
  }, [extensionGroupVariants, noneVariant]);

  useEffect(() => {
    const subscription = shop$.subscribe((shop) => {
      if (shop) {
        setShop(shop);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [shop$]);

  useEffect(() => {
    setFullFormValid(trafficAllocationValid && rulesValid && formData?.name?.length > 0);
  }, [formData.name, trafficAllocationValid, rulesValid]);

  const isNextTabTokenForShopify = useCallback(
    (currentTabName: string): boolean => {
      const nextTab = getNextTab(currentTabName);
      return nextTab === 'Token for Shopify';
    },
    [getNextTab],
  );

  const handleTabChangeWithValidation = useCallback(
    (tab: string) => {
      if (tab === 'Token for Shopify') {
        if (!testCreated) {
          return;
        }
      }
      if ((mode === 'create' || mode === 'duplicate') && activeTab === 'Token for Shopify') {
        return;
      }
      handleTabChange(tab, setShowInvalidStep);
    },
    [testCreated, mode, activeTab, handleTabChange, setShowInvalidStep],
  );

  const handleDetailsValidationCallback = useCallback(
    (tab: string) => handleDetailsValidation(tab, setShowInvalidStep),
    [handleDetailsValidation, setShowInvalidStep],
  );

  const handleRulesValidationCallback = useCallback(
    (tab: string) => handleRulesValidation(tab, setShowInvalidStep),
    [handleRulesValidation, setShowInvalidStep],
  );

  const renderTabContent = useCallback(
    (tabName: string) => {
      switch (tabName) {
        case 'Details':
          return (
            <AbTestModalDetailsTab
              formData={formData}
              mode={mode}
              showInvalidStep={showInvalidStep}
              statisticalSignificanceError={statisticalSignificanceError}
              setStatisticalSignificanceError={setStatisticalSignificanceError}
              setFormDataValue={setFormDataValue}
              handleInputChange={handleInputChange}
              onClose={onClose}
              getNextTab={getNextTab}
              handleTabChange={handleTabChangeWithValidation}
              handleDetailsValidation={handleDetailsValidationCallback}
            />
          );
        case 'Rules':
          return (
            <AbTestModalRulesTab
              formData={formData}
              mode={mode}
              showInvalidStep={showInvalidStep}
              rulesValid={rulesValid}
              widgetType={AbTestWidgetType.EXTENSION_GROUPS}
              shop={shop}
              existingAbTest={existingAbTest}
              selectedExtensionGroupIds={selectedExtensionGroupIds}
              initialRulesFromExtensionGroupsForNewABTest={initialRulesFromExtensionGroupsForNewABTest}
              filteredRulesBasedOnTestTypeCatagory={filteredRulesBasedOnTestTypeCatagory}
              checkoutApi={checkoutApi}
              showToast={showToast}
              setFormData={setFormData}
              setRulesValid={setRulesValid}
              getPreviousTab={getPreviousTab}
              getNextTab={getNextTab}
              isNextTabTokenForShopify={isNextTabTokenForShopify}
              setActiveTab={setActiveTab}
              handleTabChange={handleTabChangeWithValidation}
              handleRulesValidation={handleRulesValidationCallback}
              handleSave={handleSave}
            />
          );
        case 'Traffic allocation':
          return (
            <AbTestModalTrafficAllocationTab
              widgetType={AbTestWidgetType.EXTENSION_GROUPS}
              mode={mode}
              showInvalidStep={showInvalidStep}
              trafficAllocationValid={trafficAllocationValid}
              fullFormValid={fullFormValid}
              getPreviousTab={getPreviousTab}
              getNextTab={getNextTab}
              isNextTabTokenForShopify={isNextTabTokenForShopify}
              setActiveTab={setActiveTab}
              handleSave={handleSave}
              trafficAllocationContent={
                <ExtensionsGroupsAllocationSection
                  formData={formData}
                  setFormData={setFormData}
                  setTrafficAllocationValid={setTrafficAllocationValid}
                  mode={mode}
                  shop={shop}
                />
              }
              setShowInvalidStep={setShowInvalidStep}
              onSaveSuccess={onSaveSuccess}
            />
          );
        case 'Token for Shopify':
          return (
            <>
              <TokensForShopifySection
                formData={formData}
                extensions={extensions}
                extensionGroups={extensionGroups}
                widgetType={AbTestWidgetType.EXTENSION_GROUPS}
                savedAbTestId={savedAbTestId}
              />
              <div className="ab-test-modal__actions">
                <Button
                  onClick={() => {
                    if (mode === 'create' || mode === 'duplicate' || mode === 'view') {
                      // In create/duplicate/view mode, just close the modal
                      onSaveSuccess();
                    } else {
                      // In edit mode, save changes
                      handleSave();
                    }
                  }}
                  text={'Done'}
                  size={'medium'}
                  variant={'primary'}
                />
              </div>
            </>
          );
        default:
          return null;
      }
    },
    [
      formData,
      mode,
      showInvalidStep,
      statisticalSignificanceError,
      setStatisticalSignificanceError,
      setFormDataValue,
      handleInputChange,
      onClose,
      getNextTab,
      handleTabChangeWithValidation,
      handleDetailsValidationCallback,
      handleRulesValidationCallback,
      rulesValid,
      shop,
      existingAbTest,
      selectedExtensionGroupIds,
      initialRulesFromExtensionGroupsForNewABTest,
      filteredRulesBasedOnTestTypeCatagory,
      checkoutApi,
      showToast,
      setFormData,
      setRulesValid,
      getPreviousTab,
      isNextTabTokenForShopify,
      setActiveTab,
      handleSave,
      trafficAllocationValid,
      fullFormValid,
      extensions,
      savedAbTestId,
      onSaveSuccess,
    ],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="ab-test-modal">
        <AbTestModalHeader
          mode={mode}
          testTypeCategory={AbTestTypeCategory.EXTENSIONS_GROUPS}
          onClose={onClose}
          onTestTypeChange={onTestTypeChange}
        />
        <Tabs
          activeTab={activeTab}
          setActiveTab={handleTabChangeWithValidation}
          clickableTabs={true}
          horizontalLineUnderTabs={true}
          scrollable={true}>
          {TAB_ORDER.map((tabName) => (
            <Tab key={tabName} label={tabName} showTab={true}>
              {renderTabContent(tabName)}
            </Tab>
          ))}
        </Tabs>
      </div>
    </Modal>
  );
};
